/**
 * Driving-license cost calculator.
 *
 * Reads price configuration from the `data-prices` attribute of a
 * [data-cost-calculator] form and keeps the [data-calculator-result]
 * output in sync as the user changes the inputs.
 *
 * Usage:
 *   const form = document.querySelector<HTMLFormElement>("[data-cost-calculator]");
 *   if (form) {
 *     const calculator = new DrivingLicenseCostCalculator(form);
 *     calculator.mount();
 *   }
 *
 * Or, to wire up every calculator form on the page at once:
 *   DrivingLicenseCostCalculator.mountAll();
 */

export type CourseType = 'manual' | 'automatic';

export interface CalculatorPrices {
    manualCourse?: number;
    automaticCourse?: number;
    medicalExamination?: number;
    document?: number;
    theoryExam?: number;
    practicalExam?: number;
    additionalLesson?: number;
    schoolCarForExam?: number;
}

export interface DrivingLicenseCostCalculatorOptions {
    /** Overrides the currency read from `data-currency`. */
    currency?: string;
    /** Overrides the price config read from `data-prices`. */
    prices?: CalculatorPrices;
}

const DEFAULT_CURRENCY = 'PLN';

const FORM_SELECTOR = '[data-cost-calculator]';
const RESULT_SELECTOR = '[data-calculator-result]';

const FIELD_NAMES = {
    courseType: 'courseType',
    additionalLessons: 'additionalLessons',
    theoryAttempts: 'theoryAttempts',
    practicalAttempts: 'practicalAttempts',
    useSchoolCarForAdditionalExam: 'useSchoolCarForAdditionalExam',
} as const;

const COURSE_TYPE = {
    manual: 'manual',
    automatic: 'automatic',
} as const;

const FIELD_DEFAULTS = {
    additionalLessons: 0,
    theoryAttempts: 1,
    practicalAttempts: 1,
} as const;

export class DrivingLicenseCostCalculator {
    private readonly form: HTMLFormElement;
    private readonly prices: CalculatorPrices;
    private readonly currency: string;
    private readonly resultEl: HTMLElement | null;
    private readonly handleChange = (): void => {
        this.update();
    };
    private readonly handleNumberFieldFocus = (event: FocusEvent): void => {
        if (event.target instanceof HTMLInputElement) {
            event.target.select();
        }
    };
    private mounted = false;

    /**
     * @throws {Error} if `data-prices` is present but is not valid JSON.
     */
    constructor(form: HTMLFormElement, options: DrivingLicenseCostCalculatorOptions = {}) {
        this.form = form;
        this.currency = options.currency ?? form.dataset.currency ?? DEFAULT_CURRENCY;
        this.prices = options.prices ?? this.parsePrices(form.dataset.prices);
        this.resultEl = form.querySelector<HTMLElement>(RESULT_SELECTOR);
    }

    /** Attaches listeners and renders the initial result. Idempotent. */
    mount(): this {
        if (this.mounted) {
            return this;
        }

        this.form.addEventListener('input', this.handleChange);
        this.form.addEventListener('change', this.handleChange);

        this.getNumberFields().forEach((field) => {
            field.addEventListener('focus', this.handleNumberFieldFocus);
        });

        this.mounted = true;

        this.update();

        return this;
    }

    /** Removes listeners. Safe to call even if never mounted. */
    unmount(): this {
        if (!this.mounted) {
            return this;
        }

        this.form.removeEventListener('input', this.handleChange);
        this.form.removeEventListener('change', this.handleChange);

        this.getNumberFields().forEach((field) => {
            field.removeEventListener('focus', this.handleNumberFieldFocus);
        });

        this.mounted = false;

        return this;
    }

    /** Recalculates the total and writes it into the result element. */
    update(): number {
        const total = this.calculateTotal();

        if (this.resultEl) {
            this.resultEl.textContent = this.formatCurrency(total);
        }

        return total;
    }

    /** Finds every calculator form on the page and mounts a calculator for each. */
    static mountAll(selector: string = FORM_SELECTOR): DrivingLicenseCostCalculator[] {
        return Array.from(document.querySelectorAll<HTMLFormElement>(selector))
            .map((form) => {
                try {
                    return new DrivingLicenseCostCalculator(form).mount();
                } catch (error) {
                    console.error('[cost-calculator] Failed to initialize calculator.', error);

                    return null;
                }
            })
            .filter((instance): instance is DrivingLicenseCostCalculator => instance !== null);
    }

    private getNumberFields(): HTMLInputElement[] {
        return Array.from(this.form.querySelectorAll<HTMLInputElement>('input[type="number"]'));
    }

    private parsePrices(raw: string | undefined): CalculatorPrices {
        try {
            return JSON.parse(raw ?? '{}') as CalculatorPrices;
        } catch (error) {
            throw new Error(`[cost-calculator] Invalid price configuration: ${String(error)}`);
        }
    }

    private getNumber(name: string, fallback = 0): number {
        const field = this.form.elements.namedItem(name);
        const value =
            field instanceof HTMLInputElement || field instanceof HTMLSelectElement ? Number(field.value) : NaN;

        return Number.isFinite(value) ? value : fallback;
    }

    private getCourseType(): CourseType {
        const field = this.form.elements.namedItem(FIELD_NAMES.courseType);

        return field instanceof HTMLSelectElement && field.value === COURSE_TYPE.automatic
            ? COURSE_TYPE.automatic
            : COURSE_TYPE.manual;
    }

    private isSchoolCarForExamChecked(): boolean {
        const field = this.form.elements.namedItem(FIELD_NAMES.useSchoolCarForAdditionalExam);

        return field instanceof HTMLInputElement && field.checked;
    }

    private calculateTotal(): number {
        const courseType = this.getCourseType();

        const additionalLessons = Math.max(
            0,
            Math.floor(this.getNumber(FIELD_NAMES.additionalLessons, FIELD_DEFAULTS.additionalLessons)),
        );

        const theoryAttempts = Math.max(
            1,
            Math.floor(this.getNumber(FIELD_NAMES.theoryAttempts, FIELD_DEFAULTS.theoryAttempts)),
        );

        const practicalAttempts = Math.max(
            1,
            Math.floor(this.getNumber(FIELD_NAMES.practicalAttempts, FIELD_DEFAULTS.practicalAttempts)),
        );

        const coursePrice =
            courseType === COURSE_TYPE.automatic
                ? Number(this.prices.automaticCourse ?? 0)
                : Number(this.prices.manualCourse ?? 0);

        let total =
            coursePrice +
            Number(this.prices.medicalExamination ?? 0) +
            Number(this.prices.document ?? 0) +
            theoryAttempts * Number(this.prices.theoryExam ?? 0) +
            practicalAttempts * Number(this.prices.practicalExam ?? 0) +
            additionalLessons * Number(this.prices.additionalLesson ?? 0);

        const additionalPracticalAttempts = Math.max(0, practicalAttempts - 1);

        if (this.isSchoolCarForExamChecked() && additionalPracticalAttempts > 0) {
            total += additionalPracticalAttempts * Number(this.prices.schoolCarForExam ?? 0);
        }

        return total;
    }

    private formatCurrency(value: number): string {
        const locale = this.form.dataset.locale;

        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: this.currency,
            maximumFractionDigits: 0,
        }).format(value);
    }
}

// Expose globally: the surrounding bundle is loaded as an ES module
// (<script type="module">), so this class's top-level binding does NOT
// leak to `window` on its own. Any classic (non-module) <script> elsewhere
// on the page that references `DrivingLicenseCostCalculator` needs this.
declare global {
    interface Window {
        DrivingLicenseCostCalculator: typeof DrivingLicenseCostCalculator;
    }
}

if (typeof window !== 'undefined') {
    window.DrivingLicenseCostCalculator = DrivingLicenseCostCalculator;
}

// NOTE: mounting is triggered explicitly from the page (see the inline
// <script> in driving-license-cost.ejs calling `.mountAll()` on
// DOMContentLoaded). Do not also auto-mount here — that would attach a
// second, duplicate set of listeners to every calculator form.
